import { useEffect, useState } from "react";
import { prefetchLifelistPhotos } from "../lib/prefetch";
import type { Species, SpeciesResponse } from "../lib/types";
import { fetchSpecies, type SpeciesQuery } from "../lib/api";
import { useInstallState } from "../lib/install";
import {
  getCachedVisit,
  lifelistEntryFor,
  locationKey,
  recordVisit,
  removeFromLifelist,
  removeSpeciesFromTrip,
  toggleLifelist,
  useFishStore,
} from "../lib/storage";
import {
  useBilling,
  hasProAccess,
  FREE_LOCATION_LIMIT,
} from "../lib/billing";
import { navigate } from "../lib/router";
import { takeActiveSpot } from "../lib/activeSpot";
import { findCuratedBeach, beachKey } from "../../shared/beaches";
import { LocationForm } from "../components/LocationForm";
import { SpeciesList } from "../components/SpeciesList";
import type { SpeciesCardState } from "../components/SpeciesCard";
import { DescribeModal, type Mode } from "../components/DescribeModal";
import { LookalikeModal } from "../components/LookalikeModal";
import {
  SpeciesDetail,
  type SpeciesDetailTarget,
} from "../components/SpeciesDetail";
import { ProgressBar } from "../components/ProgressBar";
import { TripBar } from "../components/TripBar";
import { toast } from "../components/Toaster";
import { logEvent } from "../lib/events";
import { RecentSpots } from "../components/RecentSpots";
import { CameraIcon, CloseIcon, PencilIcon } from "../ui";

export function HomePage() {
  const [result, setResult] = useState<SpeciesResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [identifyMode, setIdentifyMode] = useState<Mode>("describe");
  const [compareSpecies, setCompareSpecies] = useState<Species | null>(null);
  const [detailSpecies, setDetailSpecies] =
    useState<SpeciesDetailTarget | null>(null);
  // Taxa explicitly logged this session — only used to "check" a card when
  // there's no active trip (nothing to record trip membership against).
  const [loggedNow, setLoggedNow] = useState<Set<number>>(new Set());

  // Subscribe to the lifelist so seen/checked state recomputes on changes.
  // The actual species->entry resolution goes through lifelistEntryFor,
  // which matches on the stable scientific-name key, not taxon_id alone.
  const lifelist = useFishStore((s) => s.lifelist);

  // Warm the SW photo cache with the top-N most-recent lifelist entries
  // during Home idle time — Collections then opens fully-populated from
  // cache instead of paying network for every thumb on first visit.
  // No-op on Save-Data / slow connections. Idempotent per URL.
  useEffect(() => {
    prefetchLifelistPhotos(lifelist, 12);
  }, [lifelist]);
  const activeTripId = useFishStore((s) => s.active_trip_id ?? null);
  const activeTripName = useFishStore((s) => {
    const id = s.active_trip_id;
    return id ? (s.trips.find((t) => t.id === id)?.name ?? null) : null;
  });
  const visitedLocations = useFishStore((s) => s.visited_locations);
  const recents = visitedLocations.slice(0, 6);
  const billing = useBilling();

  const isSeen = (s: Species) => lifelistEntryFor(s) !== undefined;

  // "checked" = logged on the active trip; with no active trip, only if it
  // was logged this session. Otherwise an in-lifelist species shows the
  // "seen before" outline (not checked) so it stays loggable for this trip.
  const cardStateOf = (s: Species): SpeciesCardState => {
    const entry = lifelistEntryFor(s);
    if (!entry) return "new";
    const checked = activeTripId
      ? entry.trip_ids.includes(activeTripId)
      : loggedNow.has(s.taxon_id);
    return checked ? "checked" : "lifelist";
  };

  const search = async (q: SpeciesQuery, opts?: { fromPlan?: boolean }) => {
    setLoading(true);
    setError(null);

    const source = q.name
      ? "recent_or_named"
      : q.lat !== undefined
        ? "geolocation"
        : "text";
    logEvent("search.submit", { radius_km: q.radiusKm, source });
    const t0 = performance.now();

    // Coordinate searches can be served straight from the local 24h cache.
    if (q.lat !== undefined && q.lng !== undefined) {
      const cached = getCachedVisit(q.lat, q.lng, q.radiusKm);
      if (cached) {
        setResult({
          location: { name: cached.name, lat: cached.lat, lng: cached.lng },
          radius_km: cached.radius_km,
          research_only: cached.research_only,
          species: cached.cached_species,
        });
        setLoading(false);
        logEvent("search.result", {
          cache_source: "local",
          species_count: cached.cached_species.length,
          radius_km: q.radiusKm,
          duration_ms: Math.round(performance.now() - t0),
        });
        return;
      }
    }

    try {
      const res = await fetchSpecies(q);
      // Free-tier location cap: a new place beyond the limit prompts an
      // upgrade. Re-searching one of your existing spots is always allowed,
      // and existing over-limit spots are never touched.
      const key = locationKey(res.location.lat, res.location.lng);
      const isNewLocation = !visitedLocations.some((v) => v.key === key);
      if (
        isNewLocation &&
        billing !== null &&
        !hasProAccess(billing) &&
        visitedLocations.length >= FREE_LOCATION_LIMIT
      ) {
        // A location handed over from the Plan tab just shows its species —
        // don't bounce the user to billing on a screen they didn't submit,
        // and don't record the visit against their cap. They can still see
        // what's there; adding it is gated when they actually log something.
        if (opts?.fromPlan) {
          setResult(res);
          setLoading(false);
          return;
        }
        setLoading(false);
        toast(
          `Free covers ${FREE_LOCATION_LIMIT} locations — upgrade for unlimited.`,
        );
        navigate("/billing");
        return;
      }
      setResult(res);
      recordVisit(res.location, res.radius_km, res.species, res.sort_version);
      logEvent("search.result", {
        cache_source: "network",
        is_new_location: isNewLocation,
        species_count: res.species.length,
        radius_km: q.radiusKm,
        duration_ms: Math.round(performance.now() - t0),
      });
    } catch (err) {
      const offline =
        typeof navigator !== "undefined" && navigator.onLine === false;
      setError(
        offline
          ? "You're offline. Pull up a recent spot below, or reconnect to search a new place."
          : err instanceof Error
            ? err.message
            : "Something went wrong.",
      );
      setResult(null);
      logEvent("search.error", {
        offline,
        radius_km: q.radiusKm,
        message: err instanceof Error ? err.message : String(err),
      });
    } finally {
      setLoading(false);
    }
  };

  // On mount, adopt a location handed over from the Plan tab so Spot opens
  // already populated with the spot being planned. One-shot (takeActiveSpot
  // clears it), so a later plain visit to Spot still shows the picker.
  useEffect(() => {
    const handoff = takeActiveSpot();
    if (handoff) void search(handoff, { fromPlan: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onToggle = (s: Species, photoUrl?: string) => {
    setLoggedNow((prev) => new Set(prev).add(s.taxon_id));
    const { outcome, tripName, unlocked } = toggleLifelist(
      s,
      result?.location.name ?? "an unknown location",
      photoUrl,
    );
    if (outcome === "added") {
      toast(
        tripName
          ? `Added ${s.common_name} → ${tripName}`
          : `Added ${s.common_name} to your lifelist`,
      );
    } else if (outcome === "trip-added") {
      toast(`${s.common_name} also logged to ${tripName}`);
    } else {
      toast(`${s.common_name} is already in your lifelist`);
    }
    unlocked.forEach((b) =>
      toast(`${b.threshold}% of ${b.location_name} — badge unlocked!`),
    );
  };

  // Spot-tab undo is trip-scoped: detach the active trip if the fish is on
  // it. If that was its only trip (not seen elsewhere) it leaves the lifelist
  // too. With no active trip there's nothing to leave → remove the species.
  const onRemove = (s: Species) => {
    setLoggedNow((prev) => {
      const next = new Set(prev);
      next.delete(s.taxon_id);
      return next;
    });
    // Resolve the stored entry by the stable name key — its taxon_id may
    // differ from this freshly-fetched species' id — and act on that id.
    const entry = lifelistEntryFor(s);
    const removeId = entry?.taxon_id ?? s.taxon_id;
    if (activeTripId && entry?.trip_ids.includes(activeTripId)) {
      const r = removeSpeciesFromTrip(removeId, activeTripId);
      toast(
        r === "removed"
          ? `Removed ${s.common_name} from your lifelist`
          : `Removed ${s.common_name} from ${activeTripName ?? "this trip"}`,
      );
    } else {
      removeFromLifelist(removeId);
      toast(`Removed ${s.common_name} from your lifelist`);
    }
  };

  const seenHere = result
    ? result.species.filter((s) => isSeen(s)).length
    : 0;
  const totalHere = result?.species.length ?? 0;
  const pctHere =
    totalHere > 0 ? Math.floor((seenHere / totalHere) * 100) : 0;

  return (
    <div className="mx-auto w-full max-w-3xl space-y-5 px-4 py-6 lg:max-w-5xl">
      <InstallHint />

      {!result && (
        <section className="pb-1 pt-2 text-center">
          <h1 className="text-3xl font-bold text-ink sm:text-4xl">
            What's swimming where you are?
          </h1>
          <p className="mx-auto mt-2 max-w-md text-ink/60">
            Drop a snorkel spot, see the marine life most likely there, then
            describe
            anything you can't name.
          </p>
        </section>
      )}

      {/* Controls + recents stay at a readable column width even when the
          page widens for the results grid on large screens. */}
      <div className="space-y-5 lg:mx-auto lg:w-full lg:max-w-3xl">
        <LocationForm
          onSearch={search}
          loading={loading}
          selection={
            result
              ? {
                  name: result.location.name,
                  radiusKm: result.radius_km,
                }
              : null
          }
        />

        <TripBar />

        {!result && recents.length > 0 && (
          <RecentSpots
            spots={recents}
            onOpen={(r) =>
              search({
                lat: r.lat,
                lng: r.lng,
                name: r.name,
                radiusKm: r.radius_km,
              })
            }
          />
        )}

        {error && (
          <div className="card border-l-4 border-coral p-4 text-sm text-coral">
            {error}
          </div>
        )}
      </div>

      {result && (
        <>
          <div className="space-y-5 lg:mx-auto lg:w-full lg:max-w-3xl">
            {totalHere > 0 && (
              <div className="card p-5">
                <ProgressBar
                  pct={pctHere}
                  label={`${seenHere} of ${totalHere} likely species seen here`}
                />
              </div>
            )}

            {findCuratedBeach(result.location.name) && (
              <button
                type="button"
                onClick={() =>
                  navigate(
                    `/plan/${encodeURIComponent(
                      beachKey(result.location.name),
                    )}`,
                  )
                }
                className="inline-flex items-center gap-2 rounded-full bg-aqua-deep/10 px-4 py-2 text-sm font-semibold text-aqua-deep transition hover:bg-aqua-deep/20 min-h-[44px]"
              >
                Plan a return to {result.location.name} ›
              </button>
            )}

            <div className="card p-4">
              <p className="font-semibold text-ink">Don't see it below?</p>
              <p className="text-sm text-ink/60">
                Identify it by photo, or describe it in plain language — AI
                narrows it down.
              </p>
              <div className="mt-3 flex gap-2">
                <button
                  type="button"
                  onClick={() => {
                    setIdentifyMode("photo");
                    setModalOpen(true);
                  }}
                  className="btn-accent flex-1"
                >
                  <CameraIcon className="h-4 w-4" />
                  Photo
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setIdentifyMode("describe");
                    setModalOpen(true);
                  }}
                  className="btn-ghost flex-1"
                >
                  <PencilIcon className="h-4 w-4" />
                  Describe
                </button>
              </div>
            </div>
          </div>

          <SpeciesList
            species={result.species}
            stateOf={cardStateOf}
            onToggle={onToggle}
            onRemove={onRemove}
            onCompare={setCompareSpecies}
            onInfo={(s) =>
              setDetailSpecies({
                taxon_id: s.taxon_id,
                common_name: s.common_name,
                scientific_name: s.scientific_name,
                photo_url: s.photo_url,
                location_name: result.location.name,
              })
            }
          />
        </>
      )}

      {result && (
        <DescribeModal
          open={modalOpen}
          onClose={() => setModalOpen(false)}
          initialMode={identifyMode}
          locationName={result.location.name}
          candidates={result.species}
          isSeen={isSeen}
          onAddSpecies={onToggle}
        />
      )}

      {result && (
        <LookalikeModal
          target={compareSpecies}
          locationName={result.location.name}
          candidates={result.species}
          isSeen={isSeen}
          onClose={() => setCompareSpecies(null)}
          onAddSpecies={onToggle}
        />
      )}

      <SpeciesDetail
        entry={detailSpecies}
        onClose={() => setDetailSpecies(null)}
      />
    </div>
  );
}

// One-time, dismissible "Add to Home Screen" nudge. The app is offline-capable
// and nicer installed, but that was previously only discoverable deep in
// Account settings. Shown once on Home; dismissal persists in localStorage.
function InstallHint() {
  const install = useInstallState();
  const [dismissed, setDismissed] = useState<boolean>(() => {
    try {
      return localStorage.getItem("install-hint-dismissed") === "1";
    } catch {
      return false;
    }
  });

  if (install.kind === "none" || dismissed) return null;

  const dismiss = () => {
    try {
      localStorage.setItem("install-hint-dismissed", "1");
    } catch {
      /* private mode — just hide for this session */
    }
    setDismissed(true);
  };

  return (
    <div className="card flex items-center gap-3 p-3">
      <div className="min-w-0 flex-1 text-sm text-meta">
        {install.kind === "ios" ? (
          <>
            Add Reefscope to your home screen for one-tap, offline-ready access:
            tap <b>Share</b>, then <b>Add to Home Screen</b>.
          </>
        ) : (
          <>Install Reefscope for one-tap, offline-ready access at the beach.</>
        )}
      </div>
      {install.kind === "native" && (
        <button
          type="button"
          onClick={() => void install.prompt().catch(() => {})}
          className="btn-primary shrink-0 min-h-[44px] text-sm"
        >
          Install
        </button>
      )}
      <button
        type="button"
        onClick={dismiss}
        aria-label="Dismiss install hint"
        className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full text-meta hover:bg-ink/5"
      >
        <CloseIcon className="h-4 w-4" />
      </button>
    </div>
  );
}
